import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createReseller,
  deleteReseller,
  getReseller,
  updateReseller,
} from '@/entities/admin-resellers/admin-resellers.api';
import { adminResellersQueryKeys } from '@/entities/admin-resellers/admin-resellers.keys';

const invalidateResellerQueries = (queryClient, resellerId) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: adminResellersQueryKeys.all }),
    queryClient.invalidateQueries({
      queryKey: adminResellersQueryKeys.resellers(),
    }),
    resellerId
      ? queryClient.invalidateQueries({
          queryKey: adminResellersQueryKeys.detail(resellerId),
        })
      : Promise.resolve(),
  ]);

export const useAdminResellerQuery = (id, options = {}) =>
  useQuery({
    queryKey: adminResellersQueryKeys.detail(id),
    queryFn: () => getReseller(id),
    enabled: Boolean(id),
    ...options,
  });

export const useCreateResellerMutation = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createReseller,
    ...options,
    onSuccess: async (response, payload, context) => {
      await invalidateResellerQueries(queryClient);
      await options.onSuccess?.(response, payload, context);
    },
  });
};

export const useUpdateResellerMutation = (resellerId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => updateReseller(resellerId, payload),
    ...options,
    onSuccess: async (response, payload, context) => {
      await invalidateResellerQueries(queryClient, resellerId);
      await options.onSuccess?.(response, payload, context);
    },
  });
};

export const useDeleteResellerMutation = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteReseller,
    ...options,
    onSuccess: async (response, resellerId, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminResellersQueryKeys.all }),
        queryClient.invalidateQueries({
          queryKey: adminResellersQueryKeys.resellers(),
        }),
        queryClient.removeQueries({
          queryKey: adminResellersQueryKeys.detail(resellerId),
        }),
      ]);
      await options.onSuccess?.(response, resellerId, context);
    },
  });
};
